/* English dictionary: host namespace. See src/i18n/README.md. */

import type { TextSpec } from '../text';

const host = {
  // ---- Lobby protocol (host/lobby.ts) ----
  'host.lobby.versionMismatchKick': [ 'Game versions differ; cannot share a room' ],
  'host.lobby.versionMismatch': [ "The host's game version differs from yours; you cannot join this room" ],
  'host.lobby.roomFull': [ 'Room is full (max ', { type: 'param', name: 'n' }, ')' ],
  // ---- Online table (host/online.ts) ----
  'host.online.nextGame': [ 'Next game' ],
  // ---- Rules adapter (host/rules.ts) ----
  'host.rules.missingStartOptions': [ 'Start was called without options (EkStartOptions)' ],
  'host.rules.startFailed': [ 'Start failed: ', { type: 'param', name: 'problems' } ],
  'host.rules.startFailedAction': [ 'Start failed (', { type: 'param', name: 'action' }, '): ', { type: 'param', name: 'reason' }, ' | host=', { type: 'param', name: 'host' }, ' | roster=[', { type: 'param', name: 'roster' }, ']' ],
  'host.rules.hostOnlyPresence': [ 'Only the host can change a player\u2019s connection state' ],
  'host.rules.actorMismatch': [ 'This action claims to come from ', { type: 'param', name: 'claimed' }, ', but actually came from ', { type: 'param', name: 'actual' } ],
  'host.rules.stealOrderFallback': [ 'Failed to ask the drawn player for their hand order; settling by the host-side order:' ],
  // ---- RPC (net/rpc.ts) ----
  'host.rpc.localError': [ 'local error:' ],
  'host.rpc.undecodable': [ 'Could not parse message from ', { type: 'param', name: 'from' } ],
  'host.rpc.envelopeOnNotify': [ 'Envelope ', { type: 'param', name: 't' }, ' appeared on the notification channel' ],
  'host.rpc.envelopeOnRequest': [ 'Envelope ', { type: 'param', name: 't' }, ' appeared on the request channel' ],
  'host.rpc.duplicateMethod': [ 'Method ', { type: 'param', name: 'method' }, ' is already registered; method names must be unique' ],
  'host.rpc.disposed': [ 'RPC disposed' ],
  'host.rpc.responseUndecodable': [ 'Could not parse response from ', { type: 'param', name: 'to' }, ' (', { type: 'param', name: 'method' }, ')' ],
  'host.rpc.responseNotResponse': [ 'Response from ', { type: 'param', name: 'to' }, ' is not a response envelope (', { type: 'param', name: 'method' }, ')' ],
  'host.rpc.responseIdMismatch': [ 'Response id mismatch (expected ', { type: 'param', name: 'expected' }, ', got ', { type: 'param', name: 'received' }, ')' ],
  'host.rpc.responseBadPayload': [ 'Response body from ', { type: 'param', name: 'to' }, ' does not match the contract (', { type: 'param', name: 'method' }, ')' ],
  'host.rpc.disposedPending': [ 'RPC disposed; request ', { type: 'param', name: 'method' }, ' will never get a response' ],
  'host.rpc.peerPardon': [ 'Peer pardon (', { type: 'param', name: 'method' }, '): ', { type: 'param', name: 'reason' } ],
  'host.rpc.timeout': [ { type: 'param', name: 'method' }, ' timed out' ],
  'host.rpc.aborted': [ { type: 'param', name: 'method' }, ' was aborted' ],
  'host.rpc.rejected': [ { type: 'param', name: 'method' }, ' was rejected by the peer' ],
  'host.rpc.undelivered': [ { type: 'param', name: 'method' }, ' was not delivered (connection closed)' ],
  // ---- Room-level connection layer (net/peer.ts) ----
  'host.peer.pardonReceived': [ 'Received pardon from ', { type: 'param', name: 'from' }, ': ', { type: 'param', name: 'reason' } ],
  'host.peer.heartbeatMiss': [ 'Heartbeat to ', { type: 'param', name: 'peer' }, ' failed ', { type: 'param', name: 'n' }, ' time(s): ', { type: 'param', name: 'error' } ],
  // ---- Signaling / transport (net/trystero.ts) ----
  'host.relay.noneConfigured': [ 'No signaling relays configured.' ],
  'host.relay.timeout': [ 'No signaling relay connected within ', { type: 'param', name: 'seconds' }, ' seconds.' ],
  'host.relay.allFailed': [ 'All signaling relays are unreachable.' ],
  'host.trystero.signalError': [ 'signaling error:' ],
  'host.trystero.joinFailed': [ 'Failed to join room: ', { type: 'param', name: 'error' } ],
  'host.trystero.noRequestHandler': [ 'No request handler registered' ],
  'host.trystero.disposed': [ 'Transport disposed' ],
  'host.trystero.pingTimeout': [ 'ping ', { type: 'param', name: 'id' }, ' timed out' ],
  // ---- Side channel (net/side.ts) ----
  'host.side.emptyKind': [ 'Side channel kind cannot be empty' ],
  // ---- Game-level sync (net/session.ts) ----
  'host.session.subscriberError': [ 'session: a ', { type: 'param', name: 'what' }, ' subscriber threw' ],
  'host.session.statusSubscriberError': [ 'session: a status subscriber threw' ],
  'host.session.rosterSubscriberError': [ 'session: a roster subscriber threw' ],
  'host.session.viewLabel': [ 'view' ],
  'host.session.eventLabel': [ 'event' ],
  'host.session.projectBeforeStart': [ 'Projection requested before the game started' ],
  'host.session.notHost': [ "I'm not the host" ],
  'host.session.prepareFailed': [ 'Pre-settlement query failed: ', { type: 'param', name: 'reason' } ],
  'host.session.resyncBadView': [ 'View obtained from resync does not match the contract' ],
  'host.session.resyncHashMismatch': [ 'View hash from resync also mismatches \u2014 both sides computed different views of the same state' ],
  'host.session.fullViewBad': [ 'Full view pushed by the host does not match the contract' ],
  'host.session.patchApplyFailed': [ 'Could not apply patch' ],
  'host.session.viewHashMismatch': [ 'View hash mismatch (seq ', { type: 'param', name: 'seq' }, '), falling back to full' ],
  'host.session.hostLeftRoom': [ 'Host left the room' ],
  'host.session.notInGame': [ { type: 'param', name: 'from' }, ' is not in this game' ],
  'host.session.hostEnded': [ 'Host ended this game' ],
  'host.session.pardonReceived': [ 'Received pardon from ', { type: 'param', name: 'from' }, ': ', { type: 'param', name: 'reason' } ],
  'host.session.hostPeerLeft': [ 'Host left the room' ],
  'host.session.hostWentOffline': [ 'Host went offline' ],
  'host.session.gameEnded': [ 'This game has already ended' ],
  'host.session.gameNotStarted': [ 'This game has not started yet' ],
  'host.session.hostSuspended': [ 'Host is offline, waiting for them to return' ],
  // ---- Protocol diagnostics (net/protocol.ts) ----
  'host.protocol.payloadType': [ 'payload type is ', { type: 'param', name: 'type' } ],
  // ---- PARDON reasons (net/protocol.ts stores keys; pardonText() translates) ----
  'host.pardon.badData': [ 'Could not parse the data stream' ],
  'host.pardon.badJson': [ 'Could not parse the text as JSON' ],
  'host.pardon.badMessage': [ 'Message does not match the envelope shape' ],
  'host.pardon.badRequest': [ 'Request does not match the envelope shape' ],
  'host.pardon.badResponse': [ 'Response does not match the envelope shape' ],
  'host.pardon.badMessageType': [ 'Unknown message type' ],
  'host.pardon.badMethod': [ 'Unknown method' ],
  'host.pardon.badPayload': [ 'Payload does not match the business schema' ],
  'host.pardon.internalError': [ 'An exception was thrown while dispatching the request' ],
  'host.pardon.handlerError': [ 'An exception was thrown while handling the request' ],
  'host.pardon.orphanResponse': [ 'Response has no matching request in flight' ],
  'host.pardon.accessDenied': [ 'Access denied' ],
} as const;

// 静态检查（写在对象字面量之外，避免 TextKey 循环推断）
host satisfies Record<string, TextSpec>;
export default host;
